"use client"

import * as React from "react"
import { Card, CardContent, CardHeader, CardTitle } from "./card"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./select"
import { supabase } from '@joinomu/shared'
import {
  Users,
  UserCog,
  Stethoscope,
  CreditCard,
  Gift,
  Pill,
  MessageSquare,
  Calendar,
  CalendarClock,
  CalendarX,
  Activity
} from "lucide-react"

const US_STATES = [
  { value: 'all', label: 'All States' },
  { value: 'AL', label: 'Alabama' },
  { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' },
  { value: 'CA', label: 'California' },
  { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' },
  { value: 'DE', label: 'Delaware' },
  { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' },
  { value: 'HI', label: 'Hawaii' },
  { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' },
  { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' },
  { value: 'KS', label: 'Kansas' },
  { value: 'KY', label: 'Kentucky' },
  { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' },
  { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' },
  { value: 'MN', label: 'Minnesota' },
  { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' },
  { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' },
  { value: 'NH', label: 'New Hampshire' },
  { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' },
  { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' },
  { value: 'OH', label: 'Ohio' },
  { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' },
  { value: 'PA', label: 'Pennsylvania' },
  { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' },
  { value: 'SD', label: 'South Dakota' },
  { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' },
  { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' },
  { value: 'VA', label: 'Virginia' },
  { value: 'WA', label: 'Washington' },
  { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' },
  { value: 'WY', label: 'Wyoming' },
]

interface StatsData {
  totalPatients: number
  totalProviders: number
  totalAdmins: number
  activeSubscriptions: number
  freeSubscriptions: number
  medicationSubscriptions: number
  supportFeedbackMessages24h: number
  scheduledVisits: number
  rescheduledVisits24h: number
  cancelledVisits24h: number
  totalHealthMetricEntries: number
}

interface StatCardProps {
  title: string
  value: number | string
  icon: React.ReactNode
  loading?: boolean
  filter?: React.ReactNode
}

function StatCard({ title, value, icon, loading, filter }: StatCardProps) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        {icon}
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between">
          <div className="text-2xl font-bold">
            {loading ? '...' : value}
          </div>
          {filter}
        </div>
      </CardContent>
    </Card>
  )
}

export function AdminDashboardStats() {
  const [loading, setLoading] = React.useState(true)
  const [stats, setStats] = React.useState<StatsData>({
    totalPatients: 0,
    totalProviders: 0,
    totalAdmins: 0,
    activeSubscriptions: 0,
    freeSubscriptions: 0,
    medicationSubscriptions: 0,
    supportFeedbackMessages24h: 0,
    scheduledVisits: 0,
    rescheduledVisits24h: 0,
    cancelledVisits24h: 0,
    totalHealthMetricEntries: 0,
  })
  const [patientStateFilter, setPatientStateFilter] = React.useState('all')
  const [providerStateFilter, setProviderStateFilter] = React.useState('all')
  const [activeSubsStateFilter, setActiveSubsStateFilter] = React.useState('all')
  const [freeSubsStateFilter, setFreeSubsStateFilter] = React.useState('all')
  const [medicationSubsStateFilter, setMedicationSubsStateFilter] = React.useState('all')

  // Fetch all stats
  const fetchStats = React.useCallback(async () => {
    if (!supabase) {
      console.warn('Supabase client not available')
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      const now = new Date()
      const twentyFourHoursAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000).toISOString()

      // Fetch all data in parallel
      const [
        patientsResult,
        providersResult,
        adminsResult,
        activeSubs,
        freeSubs,
        medicationSubs,
        supportMessages,
        scheduledVisitsResult,
        rescheduledVisitsResult,
        cancelledVisitsResult,
        healthMetricsResult,
      ] = await Promise.all([
        // Total patients (with optional state filter)
        patientStateFilter === 'all'
          ? supabase.from('patients').select('id', { count: 'exact', head: true })
          : supabase.from('patients').select('id', { count: 'exact', head: true }).eq('selected_state', patientStateFilter),

        // Total providers (with optional state filter)
        providerStateFilter === 'all'
          ? supabase.from('providers').select('id', { count: 'exact', head: true })
          : supabase.from('providers').select('id, licensed').contains('licensed', [providerStateFilter]),

        // Total admins (profiles with role = 'admin')
        supabase.from('profiles').select('id', { count: 'exact', head: true }).eq('role', 'admin'),

        // Active subscriptions - will be calculated separately for state filter
        Promise.resolve({ count: 0, error: null }),

        // Free subscriptions - will be calculated separately for state filter
        Promise.resolve({ data: 0, error: null }),

        // Medication subscriptions - will be calculated separately for state filter
        Promise.resolve({ count: 0, error: null }),

        // Support/feedback messages in last 24 hours
        supabase.from('support_feedback_messages')
          .select('id', { count: 'exact', head: true })
          .gte('created_at', twentyFourHoursAgo),

        // Scheduled visits (upcoming appointments)
        supabase.from('appointments')
          .select('id', { count: 'exact', head: true })
          .eq('status', 'scheduled')
          .gte('appointment_date', new Date().toISOString().split('T')[0]),

        // Rescheduled visits in last 24 hours
        supabase.from('appointments')
          .select('id', { count: 'exact', head: true })
          .eq('status', 'rescheduled')
          .gte('updated_at', twentyFourHoursAgo),

        // Cancelled visits in last 24 hours
        supabase.from('appointments')
          .select('id', { count: 'exact', head: true })
          .eq('status', 'cancelled')
          .gte('cancelled_at', twentyFourHoursAgo),

        // Total health metric tracking entries
        supabase.from('patient_health_metrics').select('id', { count: 'exact', head: true }),
      ])

      // Handle provider count with state filter (different query structure)
      let providerCount = 0
      if (providerStateFilter === 'all') {
        providerCount = providersResult.count || 0
      } else {
        // For state filter, we need to count the results
        const { data: providerData } = await supabase
          .from('providers')
          .select('id, licensed')

        if (providerData) {
          providerCount = providerData.filter((p: { licensed: string[] | null }) =>
            p.licensed && p.licensed.includes(providerStateFilter)
          ).length
        }
      }

      // Handle active subscriptions with state filter
      let activeSubCount = 0
      if (activeSubsStateFilter === 'all') {
        const { count } = await supabase
          .from('patient_medication_preferences')
          .select('patient_id', { count: 'exact', head: true })
          .eq('status', 'approved')
          .not('supply_days', 'is', null)
        activeSubCount = count || 0
      } else {
        // Get medication preferences and join with patients to filter by state
        const { data: activeSubsData } = await supabase
          .from('patient_medication_preferences')
          .select('patient_id, patients!inner(selected_state)')
          .eq('status', 'approved')
          .not('supply_days', 'is', null)
          .eq('patients.selected_state', activeSubsStateFilter)
        activeSubCount = activeSubsData?.length || 0
      }

      // Handle medication subscriptions with state filter
      let medicationSubCount = 0
      if (medicationSubsStateFilter === 'all') {
        const { count } = await supabase
          .from('patient_medication_preferences')
          .select('id', { count: 'exact', head: true })
          .eq('status', 'approved')
          .not('supply_days', 'is', null)
          .gte('next_prescription_due', new Date().toISOString().split('T')[0])
        medicationSubCount = count || 0
      } else {
        // Get medication preferences and join with patients to filter by state
        const { data: medSubsData } = await supabase
          .from('patient_medication_preferences')
          .select('id, patients!inner(selected_state)')
          .eq('status', 'approved')
          .not('supply_days', 'is', null)
          .gte('next_prescription_due', new Date().toISOString().split('T')[0])
          .eq('patients.selected_state', medicationSubsStateFilter)
        medicationSubCount = medSubsData?.length || 0
      }

      // Handle free subscriptions with state filter
      let freeSubCount = 0
      if (freeSubsStateFilter === 'all') {
        // Try RPC first
        const rpcResult = await supabase.rpc('get_free_subscription_count')
        if (rpcResult.error) {
          // Fallback: Count patients with health metrics but no active medication
          const { count: patientsWithMetrics } = await supabase
            .from('patient_health_metrics')
            .select('patient_id', { count: 'exact', head: true })

          const { count: patientsWithMeds } = await supabase
            .from('patient_medication_preferences')
            .select('patient_id', { count: 'exact', head: true })
            .eq('status', 'approved')
            .not('supply_days', 'is', null)

          freeSubCount = Math.max(0, (patientsWithMetrics || 0) - (patientsWithMeds || 0))
        } else {
          freeSubCount = rpcResult.data || 0
        }
      } else {
        // Get patients with health metrics in the selected state
        const { data: patientsWithMetricsInState } = await supabase
          .from('patient_health_metrics')
          .select('patient_id, patients!inner(selected_state)')
          .eq('patients.selected_state', freeSubsStateFilter)

        // Get patients with active medication preferences in the selected state
        const { data: patientsWithMedsInState } = await supabase
          .from('patient_medication_preferences')
          .select('patient_id, patients!inner(selected_state)')
          .eq('status', 'approved')
          .not('supply_days', 'is', null)
          .eq('patients.selected_state', freeSubsStateFilter)

        const metricsPatientIds = new Set(patientsWithMetricsInState?.map(p => p.patient_id) || [])
        const medsPatientIds = new Set(patientsWithMedsInState?.map(p => p.patient_id) || [])

        // Free subs are patients with metrics but without medication preferences
        freeSubCount = Array.from(metricsPatientIds).filter(id => !medsPatientIds.has(id)).length
      }

      setStats({
        totalPatients: patientsResult.count || 0,
        totalProviders: providerCount,
        totalAdmins: adminsResult.count || 0,
        activeSubscriptions: activeSubCount,
        freeSubscriptions: freeSubCount,
        medicationSubscriptions: medicationSubCount,
        supportFeedbackMessages24h: supportMessages.count || 0,
        scheduledVisits: scheduledVisitsResult.count || 0,
        rescheduledVisits24h: rescheduledVisitsResult.count || 0,
        cancelledVisits24h: cancelledVisitsResult.count || 0,
        totalHealthMetricEntries: healthMetricsResult.count || 0,
      })
    } catch (error) {
      console.error('Error fetching admin dashboard stats:', error)
    } finally {
      setLoading(false)
    }
  }, [patientStateFilter, providerStateFilter, activeSubsStateFilter, freeSubsStateFilter, medicationSubsStateFilter])

  React.useEffect(() => {
    fetchStats()
  }, [fetchStats])

  const StateFilter = ({ value, onChange }: { value: string; onChange: (value: string) => void }) => (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="w-[120px] h-7 text-xs">
        <SelectValue placeholder="All States" />
      </SelectTrigger>
      <SelectContent>
        {US_STATES.map((state) => (
          <SelectItem key={state.value} value={state.value} className="text-xs">
            {state.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {/* User Stats Row */}
      <StatCard
        title="Total Patients"
        value={stats.totalPatients}
        icon={<Users className="h-4 w-4 text-muted-foreground" />}
        loading={loading}
        filter={
          <StateFilter
            value={patientStateFilter}
            onChange={setPatientStateFilter}
          />
        }
      />

      <StatCard
        title="Total Providers"
        value={stats.totalProviders}
        icon={<Stethoscope className="h-4 w-4 text-muted-foreground" />}
        loading={loading}
        filter={
          <StateFilter
            value={providerStateFilter}
            onChange={setProviderStateFilter}
          />
        }
      />

      <StatCard
        title="Total Admins"
        value={stats.totalAdmins}
        icon={<UserCog className="h-4 w-4 text-muted-foreground" />}
        loading={loading}
      />

      {/* Subscription Stats Row */}
      <StatCard
        title="Active Subscriptions"
        value={stats.activeSubscriptions}
        icon={<CreditCard className="h-4 w-4 text-muted-foreground" />}
        loading={loading}
        filter={
          <StateFilter
            value={activeSubsStateFilter}
            onChange={setActiveSubsStateFilter}
          />
        }
      />

      <StatCard
        title="Free Subscriptions"
        value={stats.freeSubscriptions}
        icon={<Gift className="h-4 w-4 text-muted-foreground" />}
        loading={loading}
        filter={
          <StateFilter
            value={freeSubsStateFilter}
            onChange={setFreeSubsStateFilter}
          />
        }
      />

      <StatCard
        title="Medication Subscriptions"
        value={stats.medicationSubscriptions}
        icon={<Pill className="h-4 w-4 text-muted-foreground" />}
        loading={loading}
        filter={
          <StateFilter
            value={medicationSubsStateFilter}
            onChange={setMedicationSubsStateFilter}
          />
        }
      />

      {/* Communication Stats */}
      <StatCard
        title="Support/Feedback (24h)"
        value={stats.supportFeedbackMessages24h}
        icon={<MessageSquare className="h-4 w-4 text-muted-foreground" />}
        loading={loading}
      />

      {/* Visit Stats Row */}
      <StatCard
        title="Scheduled Visits"
        value={stats.scheduledVisits}
        icon={<Calendar className="h-4 w-4 text-muted-foreground" />}
        loading={loading}
      />

      <StatCard
        title="Rescheduled (24h)"
        value={stats.rescheduledVisits24h}
        icon={<CalendarClock className="h-4 w-4 text-muted-foreground" />}
        loading={loading}
      />

      <StatCard
        title="Cancelled (24h)"
        value={stats.cancelledVisits24h}
        icon={<CalendarX className="h-4 w-4 text-muted-foreground" />}
        loading={loading}
      />

      {/* Health Tracking Stats */}
      <StatCard
        title="Health Metric Entries"
        value={stats.totalHealthMetricEntries}
        icon={<Activity className="h-4 w-4 text-muted-foreground" />}
        loading={loading}
      />
    </div>
  )
}
